/* eslint-disable no-shadow */
import {
  JOURNAL_ALL,
  JOURNAL_AUTOCOMPLETE,
  JOURNAL_PAGE,
  JOURNAL_SEARCH,
} from "@/graphql/journal.graphql";
import {
  JournalACs,
  JournalDetailedInfo,
  JournalState,
} from "@/interfaces/journal.interface";
import {
  journalMergeField,
  journalPageReplaceField,
  journalPageState,
  journalReplaceField,
  journalState,
} from "@/stores/journal";
import { smoothScrollToTarget } from "@/utils/scroll.util";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import _ from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { statsSearch } from "./useMatomo";
import { usePubmedFetch, usePubmedLazyServer } from "./usePubmed";

export const useJournalState = () => {
  const [term, setTerm] = useRecoilState<string>(journalReplaceField("term"));
  const [journals, setJournals] = useRecoilState<JournalDetailedInfo[]>(
    journalReplaceField("journals")
  );
  const [acs, setAcs] = useRecoilState<JournalACs>(journalReplaceField("acs"));
  const [displayHelper, setDisplayHelper] = useRecoilState<
    Partial<JournalState["displayHelper"]>
  >(journalMergeField("displayHelper"));
  return {
    setTerm,
    setJournals,
    setAcs,
    setDisplayHelper,
  };
};

export const useJournal = () => {
  const { setAcs, setDisplayHelper, setTerm } = useJournalState();

  const { term, displayHelper } = useRecoilValue(journalState);

  const router = useRouter();

  const [ac, { data: acData }] = useLazyQuery<
    { searchJournal: { results: JournalACs } },
    { searchJournal: { keyword: string } }
  >(JOURNAL_AUTOCOMPLETE);

  const handleAc = _.debounce((term: string) => {
    ac({ variables: { searchJournal: { keyword: term } } });
  }, 250);

  useMemo(() => {
    if (acData) setAcs(acData.searchJournal.results);
  }, [acData]);

  const handleSearch = (term: string) => {
    setTerm(term);
    router.push(
      `/explore/journal/[term]?page=1&size=${displayHelper.size}`,
      `/explore/journal/${term}?page=1&size=${displayHelper.size}`
    );
  };

  const changePage = async ({
    page = displayHelper.page,
    size = displayHelper.size,
  }: {
    page?: number;
    size?: number;
  }) => {
    setDisplayHelper({ page, size });
    await router.push(
      `/explore/journal/[term]?page=${page}&size=${size}`,
      `/explore/journal/${term}?page=${page}&size=${size}`
    );
    smoothScrollToTarget("search-result");
  };

  return {
    handleAc,
    changePage,
    handleSearch,
  };
};

export const useJournalServer = ({
  term,
  page,
  size,
}: {
  term: string;
  page: number;
  size: number;
}) => {
  const { setJournals, setDisplayHelper, setTerm } = useJournalState();

  useMemo(() => {
    if (page) setDisplayHelper({ page });
  }, [page]);

  useMemo(() => {
    if (size) setDisplayHelper({ size });
  }, [size]);

  const { data, loading } = useQuery<
    { searchJournal: { results: JournalDetailedInfo[]; count: number } },
    { searchJournal: { keyword: string; page: number; size: number } }
  >(JOURNAL_SEARCH, {
    variables: { searchJournal: { keyword: term, page, size } },
  });

  useMemo(() => {
    if (data) {
      statsSearch(term, data.searchJournal.count, "Journal");
      setTerm(term);
      setJournals(data.searchJournal.results);
      setDisplayHelper({ count: data.searchJournal.count });
    }
  }, [data, term]);

  useEffect(() => {
    smoothScrollToTarget("search-result");
  }, [data]);

  return { loading };
};

export const useJournalAll = ({
  page,
  size,
}: {
  page: number;
  size: number;
}) => {
  const { setJournals, setDisplayHelper } = useJournalState();
  const { displayHelper } = useRecoilValue(journalState);
  const router = useRouter();
  const { data, loading, fetchMore } = useQuery<
    { allJournal: { results: JournalDetailedInfo[]; count: number } },
    { page: number; size: number }
  >(JOURNAL_ALL, {
    variables: { page, size },
  });
  useMemo(() => {
    if (data) {
      setJournals(data.allJournal.results);
      setDisplayHelper({ count: data.allJournal.count });
    }
  }, [data]);

  const changePage = ({
    page = displayHelper.page,
    size = displayHelper.size,
  }: {
    page?: number;
    size?: number;
  }) => {
    setDisplayHelper({ page, size });
    router.push(`/explore/journal?page=${page}&size=${size}`);
    fetchMore({
      variables: { page, size },
      updateQuery: (prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
    smoothScrollToTarget("search-result");
  };

  return {
    loading,
    changePage,
  };
};

export const useJournalPageState = () => {
  const [id, setId] = useRecoilState<string>(journalPageReplaceField("id"));

  const [journal, setJournal] = useRecoilState<JournalDetailedInfo>(
    journalPageReplaceField("journal")
  );

  const [activateKey, setActivateKey] = useRecoilState<number>(
    journalPageReplaceField("activateKey")
  );

  return {
    setId,
    setJournal,
    setActivateKey,
  };
};

export const useJournalPageServer = ({ id }: { id: string }) => {
  const { setId, setJournal } = useJournalPageState();
  const { id: prevId } = useRecoilValue(journalPageState);
  const { data, loading } = useQuery<
    { getJournal: { id: string; journal: JournalDetailedInfo } },
    { id: string }
  >(JOURNAL_PAGE, { variables: { id }, skip: prevId === id });

  useMemo(() => {
    if (data) {
      setId(data?.getJournal.id);
      setJournal(data?.getJournal.journal);
    }
  }, [data, id]);

  return { loading };
};

export const useJournalPapers = ({
  page,
  size,
  fetchParams,
}: {
  page: number;
  size: number;
  fetchParams: { retstart: number; retmax: number };
}) => {
  const { journal } = useRecoilValue(journalPageState);
  const { search, loading: loadingSearch } = usePubmedLazyServer({
    page,
    size,
    fetchParams: { ...fetchParams, term: `${journal.issn}[journal]` },
  });
  const { handleFetch, loading: loadingFetch } = usePubmedFetch();

  return {
    search,
    handleFetch,
    loadingSearch,
    loadingFetch,
  };
};
